import { parseSSEBuffer } from "../applyStreamEvent";
import { getJWTClient } from "../connectClient";

import {
  PropagateProgressSchema,
  PropagateRequestSchema,
  PublishProgressSchema,
  type PropagateProgress,
  type PropagateRequest,
  type PublishProgress,
  type PublishRequest,
} from "@/types/propagation";
import { blockDemoMutation, isDemoUserFromStorage } from "@/utils/demoMode";

const API_BASE_URL: string = import.meta.env.VITE_CREATIVESPHERE_API_URL || "";

interface InterpretedFrame {
  error?: string;
  done?: boolean;
  /** The raw progress object to be validated by the caller's schema. */
  payload?: Record<string, unknown>;
}

/**
 * Interprets a single parsed SSE event object for the propagate/publish streams.
 *
 * This is the ONLY place that encodes assumptions about the backend event shape,
 * so adjusting to the real contract means editing just this function. Shared by
 * both streams because their frames are structurally identical (a
 * `{ total, completed, item, status }` progress object). It tolerates:
 *   - progress: the progress object directly, `{ type: "progress", ... }`, or a
 *               wrapped `{ progress: {...} }` / `{ data: {...} }`.
 *   - terminal: `{ type: "done"|"complete" }` or `{ done: true }`.
 *   - error:    `{ type: "error" }` or any `{ error: string }`.
 *
 * Exported for unit testing independently of the network read loop.
 */
export function interpretPropagationFrame(obj: unknown): InterpretedFrame {
  if (!obj || typeof obj !== "object") return {};
  const event = obj as Record<string, unknown>;
  const type = typeof event.type === "string" ? event.type : undefined;

  if (type === "error" || typeof event.error === "string") {
    const message =
      typeof event.error === "string" && event.error.trim()
        ? event.error
        : "Stream failed";
    return { error: message };
  }

  // Unwrap a wrapped progress payload; otherwise treat the event itself as one.
  const wrapped =
    event.progress && typeof event.progress === "object"
      ? (event.progress as Record<string, unknown>)
      : event.data && typeof event.data === "object"
        ? (event.data as Record<string, unknown>)
        : undefined;
  const payload = wrapped ?? event;

  const terminal =
    type === "done" || type === "complete" || event.done === true;

  // A frame is "progress" if it carries any of the progress fields.
  const hasProgress =
    "total" in payload ||
    "completed" in payload ||
    "item" in payload ||
    "status" in payload;

  return {
    ...(terminal ? { done: true } : {}),
    ...(hasProgress ? { payload } : {}),
  };
}

/**
 * Splits the stream body into event payloads. The publish endpoint sends raw
 * JSON objects back to back (`{...}{...}`, no `data:` lines), so a body that
 * starts with `{` is split on top-level objects; anything else is parsed as SSE.
 */
export function parseStreamBuffer(buffer: string): {
  events: string[];
  rest: string;
} {
  if (!buffer.trimStart().startsWith("{")) return parseSSEBuffer(buffer);
  const events: string[] = [];
  let depth = 0;
  let start = 0;
  let consumed = 0;
  let inString = false;
  let escaped = false;
  for (let i = 0; i < buffer.length; i++) {
    const ch = buffer[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === "\\") escaped = true;
      else if (ch === '"') inString = false;
    } else if (ch === '"') {
      inString = true;
    } else if (ch === "{") {
      if (depth === 0) start = i;
      depth++;
    } else if (ch === "}" && depth > 0) {
      depth--;
      if (depth === 0) {
        events.push(buffer.slice(start, i + 1));
        consumed = i + 1;
      }
    }
  }
  return { events, rest: buffer.slice(consumed) };
}

/**
 * A progress frame is terminal once every item has resolved (`completed` has
 * caught up to `total`). Only an *unclean* close consults this: a reset after the
 * run had effectively finished is tolerated, while a mid-run reset still rejects.
 *
 * An empty collection reports `total: 0` — there is nothing to process, so its
 * sole frame is already terminal. This assumes `total: 0` only ever means "empty
 * run", never a "count-not-yet-known" placeholder streamed ahead of real frames.
 */
function isTerminalProgress(progress: {
  total: number;
  completed: number;
}): boolean {
  return progress.total === 0 || progress.completed >= progress.total;
}

/**
 * Shared POST → SSE read loop for the propagate/publish streams. Reads the body
 * with `fetch` + a `ReadableStream` reader (the axios JWT client buffers the whole
 * body and can't surface progress mid-run). Each validated progress object is
 * pushed to `updates` and passed to `onProgress`; the full ordered list is
 * returned so callers can inspect the terminal frame.
 *
 * A run resolves on a clean EOF — the backend often ends a successful run by
 * closing the socket with no explicit terminal frame (`{done:true}`/`complete`,
 * or a progress frame whose `completed` reached `total`), so a missing terminal
 * frame can't be treated as a failure. A backend-signalled error frame, an abort,
 * and a mid-run network reset before any terminal frame still reject.
 *
 * TOKEN LIFETIME: `fetch` bypasses the JWT client's axios auto-refresh
 * interceptor, so the bearer captured at open time is fixed for the whole run —
 * it is never refreshed mid-stream. A single connection must therefore complete
 * within the token's remaining TTL (the JWT client refreshes on a background
 * timer, so the token is usually near-fresh at open, but not guaranteed). If a
 * run runs longer than that window it will fail with a mid-stream 401/network
 * error rather than transparently refreshing.
 *
 * This is assumed safe because a propagate/publish run is expected to be short
 * (seconds to low minutes) relative to the access-token TTL. If backend runs are
 * ever expected to approach the TTL, force a refresh before opening the stream
 * (e.g. a pre-flight authenticated request through `withJWTClient`, or a
 * `getJWTClient()` refresh call) instead of waiting for the mid-stream 401 — this
 * function is the single place to add that.
 */
async function streamSSE<T>(
  url: string,
  body: unknown,
  parse: (raw: unknown) => T,
  isTerminal: (progress: T) => boolean,
  onProgress: ((progress: T) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<T[]> {
  // A mutation in effect — enforce the demo block here since this fetch bypasses
  // the guarded JWT client.
  if (isDemoUserFromStorage()) return blockDemoMutation();
  const token = getJWTClient()?.currentJWT?.token;
  if (!token) throw new Error("JWT client not available");

  // eslint-disable-next-line demo-guard/no-unguarded-requests -- SSE stream; demo-blocked above
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(
      `Failed to open stream: ${response.status} ${response.statusText}`,
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const updates: T[] = [];
  // Set by a backend-signalled error frame; rejects the run even after progress.
  let streamError: Error | undefined;
  // Set once a terminal frame arrives; a late unclean close is only tolerated
  // after this point.
  let terminalReached = false;

  // Returns true when the read loop should stop: a backend error, or an explicit
  // terminal frame. A `completed === total` frame marks the run terminal but does
  // NOT stop the loop — we let the server close so a trailing frame isn't dropped.
  const handleEvent = (raw: string): boolean => {
    if (!raw || raw === "[DONE]") return false;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return false; // ignore keep-alives / non-JSON comments
    }
    const frame = interpretPropagationFrame(parsed);
    if (frame.error) {
      streamError = new Error(frame.error);
      return true;
    }
    if (frame.payload) {
      const progress = parse(frame.payload);
      updates.push(progress);
      onProgress?.(progress);
      if (isTerminal(progress)) terminalReached = true;
    }
    // An explicit `{type:"done"|"complete"}` / `{done:true}` frame: mark terminal
    // and stop, so a backend that holds the socket open with keep-alive comments
    // afterwards (common behind proxies) doesn't strand the caller in `isPending`.
    if (frame.done) {
      terminalReached = true;
      return true;
    }
    return false;
  };

  let stopped = false;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (value) {
        buffer += decoder.decode(value, { stream: true });
        const { events, rest } = parseStreamBuffer(buffer);
        buffer = rest;
        for (const raw of events) {
          if (handleEvent(raw)) {
            stopped = true;
            break;
          }
        }
      }
      if (stopped || done) break;
    }
    if (!stopped) {
      // Flush a trailing frame the stream may have closed without a final blank line.
      buffer += decoder.decode();
      for (const raw of parseStreamBuffer(`${buffer}\n\n`).events) {
        if (handleEvent(raw)) break;
      }
    }
  } catch (error) {
    void reader.cancel().catch(() => {});
    // Tolerate a late unclean close (proxy RST, missing terminal chunk) ONLY once
    // a terminal frame was already seen — the run had effectively finished. An
    // abort, a mid-run reset, or a malformed frame before the terminal one must
    // surface rather than resolve with a partial list.
    if (!terminalReached) throw error;
    return updates;
  }

  void reader.cancel().catch(() => {});

  // A backend-signalled error rejects even when progress frames preceded it.
  if (streamError) throw streamError;

  // A clean EOF is the end of the run, whether or not a terminal frame arrived:
  // the backend routinely finishes a successful run by just closing the socket,
  // without a trailing `{done}` frame and without a progress frame whose
  // `completed` reached `total`. Only a backend error frame, a failed open, or an
  // unclean close before a terminal frame reject. Callers must therefore treat the
  // returned frames as "what the run reported", not "the run succeeded" — the
  // authoritative outcome is the propagation's own state, which the hooks
  // invalidate on settle (see `useStreamPropagate` / `useStreamPublish`).
  return updates;
}

/**
 * POST /v1/stream/propagate — applies a seeds_map across a target collection (or
 * drives an existing propagation) and streams `PropagateProgress` frames. Returns
 * every progress update in order; the final one carries the terminal status
 * (e.g. `COLLECTION_REVIEW`).
 */
export async function streamPropagate(
  req: PropagateRequest,
  opts: {
    onProgress?: (progress: PropagateProgress) => void;
    signal?: AbortSignal;
  } = {},
): Promise<PropagateProgress[]> {
  const parsed = PropagateRequestSchema.safeParse(req);
  if (!parsed.success) {
    throw new Error(
      parsed.error.issues[0]?.message ??
        "PropagateRequest requires at least one of target_collection_id or propagation_slug",
    );
  }
  return streamSSE(
    `${API_BASE_URL}/v1/stream/propagate`,
    parsed.data,
    (raw) => PropagateProgressSchema.parse(raw),
    isTerminalProgress,
    opts.onProgress,
    opts.signal,
  );
}

/**
 * POST /v1/stream/publish — publishes the accepted items of a propagation and
 * streams `PublishProgress` frames. Returns every progress update in order; the
 * final one carries the terminal status (e.g. `PUBLISHED`).
 */
export async function streamPublish(
  req: PublishRequest,
  opts: {
    onProgress?: (progress: PublishProgress) => void;
    signal?: AbortSignal;
  } = {},
): Promise<PublishProgress[]> {
  return streamSSE(
    `${API_BASE_URL}/v1/stream/publish`,
    req,
    (raw) => PublishProgressSchema.parse(raw),
    isTerminalProgress,
    opts.onProgress,
    opts.signal,
  );
}
